/**
 * @fileoverview Hook para detectar scroll horizontal e adicionar sombras visuais
 * 
 * @module useHorizontalScroll
 * @description
 * Hook customizado que detecta se um elemento tem scroll horizontal
 * e retorna estados para mostrar sombras esquerda/direita
 * 
 * @features
 * - Detecta overflow horizontal
 * - Retorna showLeftShadow e showRightShadow
 * - Auto-atualiza em resize e scroll
 * - Performance otimizada com throttle
 * 
 * @usage
 * ```tsx
 * const { containerRef, showLeftShadow, showRightShadow } = useHorizontalScroll();
 * 
 * <Box ref={containerRef} sx={{ overflowX: 'auto' }}>
 *   {showLeftShadow && <LeftShadow />}
 *   {showRightShadow && <RightShadow />}
 *   <Table>...</Table>
 * </Box>
 * ```
 */

import { useEffect, useRef, useState } from 'react';

interface UseHorizontalScrollReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  showLeftShadow: boolean;
  showRightShadow: boolean;
}

export function useHorizontalScroll(): UseHorizontalScrollReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      
      // Show left shadow if scrolled right
      setShowLeftShadow(scrollLeft > 0);
      
      // Show right shadow if not at the end (with 1px tolerance)
      setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 1);
    };

    // Check initial state
    checkScroll();

    // Listen to scroll events
    container.addEventListener('scroll', checkScroll);
    
    // Listen to resize events (table might change size)
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      resizeObserver.disconnect();
    };
  }, []);

  return { containerRef, showLeftShadow, showRightShadow };
}
